import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import AppButton from '@/components/ui/AppButton';
import { Label } from '@/components/ui/label';
import { toast } from '@/lib/swal';
import { Facebook, Save, Trash2, Info, CheckCircle2, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MarketingManager = () => {
  const [pixelId, setPixelId] = useState('');
  const [savedPixelId, setSavedPixelId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/marketing`);
      const id = res.data?.facebook_pixel_id || '';
      setPixelId(id);
      setSavedPixelId(id);
    } catch (error) {
      console.error('Error cargando config de marketing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/settings/marketing`, {
        facebook_pixel_id: pixelId.trim(),
      });
      setSavedPixelId(res.data.facebook_pixel_id || '');
      setPixelId(res.data.facebook_pixel_id || '');
      toast.success('Píxel de Facebook guardado correctamente');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al guardar el píxel';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/marketing`, { facebook_pixel_id: '' });
      setPixelId('');
      setSavedPixelId('');
      toast.success('Píxel de Facebook eliminado');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al eliminar el píxel';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full space-y-6" data-testid="marketing-manager">
      <Card className="border-none shadow-2xl bg-white dark:bg-[#0a0a0a] overflow-hidden rounded-[2rem]">
        {/* Header Premium */}
        <div className="bg-gradient-to-r from-slate-900 via-[#111] to-[#020202] p-8 text-white relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-gradient-to-l from-[#1877F2]/20 to-transparent opacity-50 pointer-events-none"></div>
          
          <div className="flex items-center gap-5 relative z-10">
            <div className="w-16 h-16 rounded-[1.25rem] bg-gradient-to-br from-[#1877F2] to-[#0d47a1] flex items-center justify-center shadow-[0_0_30px_rgba(24,119,242,0.4)] shrink-0 border border-white/10">
              <Facebook size={32} className="text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-black italic tracking-tighter uppercase text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
                Píxel de Meta
              </h2>
              <p className="text-slate-400 font-medium text-sm mt-1">Sincronización de eventos de conversión y retargeting</p>
            </div>
          </div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-2 bg-white/5 backdrop-blur-md px-5 py-2.5 rounded-2xl border border-white/10 shadow-inner">
              {loading ? (
                <span className="text-sm text-slate-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-[#fca050]" /> Sincronizando...</span>
              ) : savedPixelId ? (
                <span className="text-sm text-emerald-400 flex items-center gap-2 font-bold"><CheckCircle2 className="w-5 h-5 drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]" /> Píxel Conectado: <span className="text-white tracking-wider">{savedPixelId}</span></span>
              ) : (
                <span className="text-sm text-slate-400 font-medium">Estado: <span className="text-rose-400 font-bold">Sin configurar</span></span>
              )}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* Left Column: Input Form */}
          <div className="lg:col-span-5 space-y-6">
            <div>
              <h3 className="text-xl font-black uppercase tracking-tighter mb-2 dark:text-white">Conecta tu Píxel</h3>
              <p className="text-muted-foreground text-sm">
                Pega el ID de tu píxel de Meta (15-16 dígitos) para comenzar a enviar datos de actividad automáticamente.
              </p>
            </div>
            
            <div className="space-y-3">
              <Label className="text-[11px] font-black uppercase tracking-widest text-[#f24e00]">ID de Identificación</Label>
              <div className="relative">
                <Input
                  placeholder="Ej: 1234567890123456"
                  value={pixelId}
                  onChange={(e) => setPixelId(e.target.value)}
                  className="h-14 pl-4 pr-12 rounded-2xl focus-visible:ring-[#fca050] border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 font-mono text-lg shadow-inner transition-all"
                  data-testid="pixel-id-input"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <AppButton
                variant="primary"
                className="flex-1 h-12 rounded-xl text-xs font-black tracking-widest uppercase shadow-lg shadow-[#f24e00]/20 bg-gradient-to-r from-[#f24e00] to-[#fca050] hover:from-[#d14000] hover:to-[#f24e00] border-none text-white"
                onClick={handleSave}
                loading={saving}
                disabled={pixelId.trim() === savedPixelId}
                icon={Save}
                data-testid="pixel-save-btn"
              >
                Guardar Píxel
              </AppButton>
              
              {savedPixelId && (
                <Button
                  variant="outline"
                  className="h-12 px-6 rounded-xl border-rose-200 text-rose-600 hover:bg-rose-50 dark:border-rose-900/40 dark:hover:bg-rose-950/30 text-xs font-black tracking-widest uppercase transition-all"
                  onClick={handleDelete}
                  disabled={saving}
                  data-testid="pixel-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Desconectar
                </Button>
              )}
            </div>
          </div>
          
          {/* Right Column: Tracking Details */}
          <div className="lg:col-span-7 bg-slate-50 dark:bg-white/[0.02] rounded-3xl p-6 md:p-8 border border-slate-100 dark:border-white/5 shadow-sm">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200 dark:border-white/10">
              <div className="w-10 h-10 rounded-full bg-[#1877F2]/10 flex items-center justify-center">
                <Info className="w-5 h-5 text-[#1877F2]" />
              </div>
              <div>
                <h3 className="text-sm font-black uppercase tracking-widest dark:text-white">¿Qué eventos registra este Píxel?</h3>
                <p className="text-xs text-muted-foreground mt-0.5">El código está pre-programado para medir automáticamente:</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div><span className="text-xs font-bold uppercase tracking-wider">Visitas & Navegación</span></div>
                <p className="text-[11px] text-muted-foreground pl-3.5">
                  <strong className="text-slate-700 dark:text-slate-300">PageView:</strong> Páginas visitadas.<br/>
                  <strong className="text-slate-700 dark:text-slate-300">ViewContent:</strong> Cuando ven el sorteo.<br/>
                  <strong className="text-slate-700 dark:text-slate-300">Scroll:</strong> Porcentajes del 25%, 50%, 75% y 90%.
                </p>
              </div>
              
              <div className="space-y-1">
                <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div><span className="text-xs font-bold uppercase tracking-wider">Tiempo en Página</span></div>
                <p className="text-[11px] text-muted-foreground pl-3.5">
                  Mide el interés real del usuario disparando eventos en hitos exactos de: <strong className="text-[#1877F2]">15s, 30s, 60s y 120s</strong>.
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div><span className="text-xs font-bold uppercase tracking-wider">Intención de Compra</span></div>
                <p className="text-[11px] text-muted-foreground pl-3.5">
                  <strong className="text-slate-700 dark:text-slate-300">SelectPackage / AddToCart:</strong> Paquete elegido con divisa (USD) y monto.<br/>
                  <strong className="text-slate-700 dark:text-slate-300">InitiateCheckout:</strong> Apertura de modal con divisa (USD) y valor.<br/>
                  <strong className="text-slate-700 dark:text-slate-300">FormStarted:</strong> Al escribir el correo.
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div><span className="text-xs font-bold uppercase tracking-wider">Conversión Final</span></div>
                <p className="text-[11px] text-muted-foreground pl-3.5">
                  <strong className="text-slate-700 dark:text-slate-300">AddPaymentInfo:</strong> Selección de método con divisa (USD) y monto.<br/>
                  <strong className="text-emerald-600 dark:text-emerald-400">Purchase:</strong> Confirmación de pago con divisa (USD) y valor en la página de gracias.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MarketingManager;
